import { Inject, Injectable } from '@angular/core';
import { createSelector, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, filter, Observable, of, take, tap } from 'rxjs';

import { fromCarl } from 'src/app/store/common/common.selectors';
import { WINDOW } from 'src/app/utils/window.token';

import { OWCC_SESSION_STORAGE_KEY } from '../../config/web-storage.config';
import { ScrollingService } from '../scrolling/scrolling.service';
import { SessionStorageService } from '../storage/session-storage/session-storage.service';

export interface AcknowledgementConfig {
  isActive: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AcknowledgementService {
  public readonly selectMessageListTranslations = createSelector(
    fromCarl.selectMultipleTranslations<{ agreementMessage: string }>('acknowledgement'),
    acknowledgement => acknowledgement?.agreementMessage
  );
  public readonly translations$ = this.store.select(this.selectMessageListTranslations);

  private readonly activateAcknowledgementLayerSubject$ = new BehaviorSubject<boolean>(false);

  public constructor(
    private readonly store: Store,
    private readonly scrollingService: ScrollingService,
    private readonly sessionStorageService: SessionStorageService,
    @Inject(WINDOW) private readonly window: Window
  ) {}

  public init(): void {
    const isAcknowledgementAccepted$ = of(this.sessionStorageService.get(OWCC_SESSION_STORAGE_KEY.ACKNOWLEDGEMENT));
    const acknowledgementConfig$ = this.store
      .pipe(fromCarl.selectConfigOnce<AcknowledgementConfig>('acknowledgement'))
      .pipe(
        take(1),
        filter(acknowledgementConfig => !!acknowledgementConfig)
      );

    combineLatest([acknowledgementConfig$, isAcknowledgementAccepted$])
      .pipe(
        tap(([acknowledgementConfig, isAcknowledgementAccepted]) => {
          if (acknowledgementConfig.isActive && !isAcknowledgementAccepted) {
            this.activateAcknowledgementLayerSubject$.next(true);
          }
        })
      )
      .subscribe();
  }

  public listenToModalStateChanges(): Observable<boolean> {
    return this.activateAcknowledgementLayerSubject$.asObservable();
  }

  public onUserAcknowledgementResponse(accepted: boolean): void {
    this.sessionStorageService.set(OWCC_SESSION_STORAGE_KEY.ACKNOWLEDGEMENT, accepted);
    this.activateAcknowledgementLayerSubject$.next(false);

    if (!accepted) {
      this.window.history.back();
    }

    this.scrollingService.enableScrolling();
    this.scrollingService.enableScrollingMobile();
  }
}
